'use client';

import React, { FC } from 'react';
import { useInView } from 'react-intersection-observer';
import createAnimationArr from '@/src/lib/helpers/createAnimationArr';
import styles from './FooterCTA.module.scss';
import NextImage from '@/src/components/ui/nextImage/NextImage';
import Button from '@/src/components/ui/button/Button';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/free-mode';

import imageHelper from '@/src/lib/helpers/imageHelper';
import { ITheme } from '@/src/lib/types/themeInterface/index';
import ShopifyStoreButton from '@/src/components/ui/shopifyButton/ShopifyStoreButton';

type TCTA = {
  theme: ITheme;
};

const FooterCTA: FC<TCTA> = ({ theme }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0,
  });

  const desktop = `(max-width: 376px) 360px, (max-width: 414px) 400px, (max-width: 576px) 560px, 
  (max-width: 768px) 480px, (max-width: 820px) 560px, (max-width: 1024px) 43.23vw, 
  (max-width: 1700px) 36.025vw, 43.23vw`;

  const mobile = `(max-width: 376px) 90px, (max-width: 414px) 100px, (max-width: 576px) 23vw,
  (max-width: 768px) 120px, (max-width: 820px) 140px, (max-width: 1024px) 10.83vw, 
  (max-width: 1700px) 9.025vw, 10.83vw`;

  const initialSpeed = 18000;

  const isStockist = theme.title?.toLowerCase() === 'stockist';
  const isPrompt = theme.title?.toLowerCase() === 'prompt';

  return (
      <section className={`${styles.cta} ${isStockist ? styles.cta_stockist : (isPrompt ? styles.cta_prompt : '')}`}>

          <div className={styles.cta__info} ref={ref}>
              <h2 className={`heading__1 ${styles.heading} ${inView && styles.animate__heading}`}>
                  {theme.footerCtaSectionTitle &&
                      createAnimationArr(theme.footerCtaSectionTitle, styles.word__wrapper, 0, 0.01)}

                  {isStockist && (
                      <svg
                          width="314"
                          height="36"
                          viewBox="0 0 314 36"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg">
                          <path
                              d="M4 12.1405C41.3765 7.88454 152.288 0.649439 296.923 5.75657"
                              stroke="#F698B7"
                              strokeWidth="8"
                              strokeLinecap="round"
                          />
                          <path
                              d="M85.0119 24.6094C111.576 19.7938 191.073 9.33486 296.545 6.02344"
                              stroke="#F698B7"
                              strokeWidth="8"
                              strokeLinecap="round"
                          />
                          <path
                              d="M85.8921 24.5209C121.827 22.1925 173.348 17.7982 309.787 29.2735"
                              stroke="#F698B7"
                              strokeWidth="8"
                              strokeLinecap="round"
                          />
                      </svg>
                  )}
              </h2>
              <p className={`excerpt ${styles.excerpt} ${inView && styles.animate__excerpt}`}>
                  {theme.footerCtaSectionDescription &&
                      createAnimationArr(theme.footerCtaSectionDescription, styles.word__wrapper, 0.1, 0.01)}
              </p>

              <div className={styles.buttons}>
                  <ShopifyStoreButton isWhite={true} theme={theme}/>
              </div>
          </div>
          <Swiper
              modules={[Autoplay]}
              slidesPerView="auto"
              allowTouchMove={false}
              shortSwipes={false}
              longSwipes={false}
              className={`${styles.swiper} gallery-autoplay-swiper`}
              loop={true}
              autoplay={{
                  disableOnInteraction: false,
                  pauseOnMouseEnter: false,
                  delay: 0,
              }}
              speed={initialSpeed}
              onSlideChange={(swiper) => {
                  const active = swiper.realIndex;
                  swiper.params.speed =
                      active === 1 || active === 5 || active === 9 || active === 13
                          ? initialSpeed / 3.99
                          : initialSpeed;
              }}>
              {theme.footerCtaGallery &&
                  [...theme.footerCtaGallery, ...theme.footerCtaGallery].map((item, i) => {
                      return (
                          <SwiperSlide
                              key={`{slide${i}}-${item.galleryImageSize}`}
                              className={`${styles.slide} ${styles[`${item.galleryImageSize}`]}`}>
                              <div className={styles.slide__content}>
                                  <NextImage
                                      sizes={item.galleryImageSize}
                                      src={imageHelper(item.galleryImage).url}
                                      alt={`cta-image-${item.galleryImageSize}`}
                                      fill
                                  />
                              </div>
                          </SwiperSlide>
                      );
                  })}
          </Swiper>
      </section>
  );
};

export default FooterCTA;
